<template>
  <div class="manuals-panel">
    <!-- ══ VISTA 1: Pastas por projeto ══ -->
    <template v-if="!selectedProject">
      <div class="manuals-header">
        <h2 class="manuals-title">Manuais <span class="beta-tag">Beta</span></h2>
        <p class="manuals-subtitle">Manuais de operação e manutenção em PDF, organizados por obra. O EstagIArio Joe pode interpretar rotinas e responder dúvidas.</p>
      </div>

      <div v-if="loadingProjects" class="manuals-loading">Carregando projetos...</div>
      <div v-else-if="projects.length === 0" class="manuals-empty">Nenhum projeto cadastrado.</div>

      <div v-else class="folder-grid">
        <button v-for="proj in projects" :key="proj.id" class="folder-card" type="button" @click="openFolder(proj)">
          <div class="folder-icon">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
          </div>
          <div class="folder-body">
            <div class="folder-name">{{ proj.name }}</div>
            <div class="folder-count">{{ manualCountFor(proj.id) }} {{ manualCountFor(proj.id) === 1 ? 'manual' : 'manuais' }}</div>
          </div>
        </button>
      </div>
    </template>

    <!-- ══ VISTA 2: Pasta do projeto ══ -->
    <template v-else>
      <div class="folder-header">
        <button class="folder-back" type="button" @click="closeFolder">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          Voltar
        </button>
        <h2 class="folder-title">{{ selectedProject.name }}</h2>
      </div>

      <div class="folder-tabs">
        <button class="folder-tab" :class="{ active: activeTab === 'manuais' }" type="button" @click="activeTab = 'manuais'">Manuais</button>
        <button v-if="aiEnabled" class="folder-tab folder-tab--ia" :class="{ active: activeTab === 'duvidas' }" type="button" @click="openDuvidas">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Alguma dúvida?
        </button>
      </div>

      <!-- ── Aba Manuais ── -->
      <div v-if="activeTab === 'manuais'" class="folder-body-area">
        <div class="manuals-actions">
          <button class="btn-upload" type="button" @click="openUploadModal">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Enviar manual (PDF)
          </button>
        </div>

        <div v-if="manualsStore.state.loading" class="manuals-loading">Carregando manuais...</div>
        <div v-else-if="folderManuals.length === 0" class="manuals-empty">
          Nenhum manual nesta obra ainda. Envie o manual de operação e manutenção da atração em PDF.
        </div>

        <div v-else class="manual-list">
          <div v-for="manual in folderManuals" :key="manual.id" class="manual-card">
            <div class="manual-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div class="manual-info">
              <div class="manual-name">{{ manual.name }}</div>
              <div class="manual-meta">
                <span>{{ manual.pageCount || '?' }} pág.</span>
                <span class="meta-sep">·</span>
                <span>{{ formatSize(manual.sizeBytes) }}</span>
                <span v-if="manual.originalSizeBytes > manual.sizeBytes" class="compressed-tag" :title="`Original: ${formatSize(manual.originalSizeBytes)}`">comprimido</span>
                <span v-if="!manual.textOk" class="scan-tag" title="PDF escaneado — sem camada de texto">escaneado</span>
              </div>
              <div class="manual-meta manual-meta--sub">
                Enviado por {{ manual.uploadedBy }} em {{ formatDate(manual.createdAt) }}
              </div>
            </div>
            <div class="manual-actions">
              <button class="m-act" type="button" title="Ver PDF" @click="viewPdf(manual)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
              <button class="m-act" type="button" title="Editar" @click="openEditModal(manual)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <button class="m-act m-act--danger" type="button" title="Excluir" @click="askRemove(manual)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
              <!-- Já enviado neste projeto: botão some, entra o resultado -->
              <template v-if="aiEnabled && extractionFor(manual)">
                <div class="estagiario-result">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>{{ extractionFor(manual)!.count }} rotina{{ extractionFor(manual)!.count !== 1 ? 's' : '' }} criada{{ extractionFor(manual)!.count !== 1 ? 's' : '' }} pelo estagiário em {{ formatDate(extractionFor(manual)!.at) }}</span>
                </div>
                <button class="btn-ver-rotinas" type="button" @click="goToRoutines">Ver rotinas</button>
                <button class="btn-reenviar" type="button" :disabled="extracting" title="Interpretar o manual novamente (cria rotinas adicionais)" @click="sendToEstagiario(manual)">reenviar</button>
              </template>
              <button v-else-if="aiEnabled" class="btn-estagiario" type="button" :disabled="extracting" @click="sendToEstagiario(manual)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                Enviar para o estagiário
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Aba Alguma dúvida? ── -->
      <div v-else-if="activeTab === 'duvidas'" class="duvidas-area">
        <div v-if="folderManuals.length === 0" class="manuals-empty">Envie um manual primeiro para tirar dúvidas.</div>
        <template v-else>
          <div v-if="folderManuals.length > 1" class="duvidas-manual-picker">
            <span class="picker-label">Manual:</span>
            <button v-for="m in folderManuals" :key="m.id" class="picker-chip" :class="{ active: chatManualId === m.id }" type="button" @click="switchChatManual(m.id)">
              {{ m.name }}
            </button>
          </div>

          <div ref="chatScroll" class="chat-messages">
            <div v-if="chatMessages.length === 0" class="chat-hero">
              <div class="chat-hero-icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <h3 class="chat-hero-title">O que você quer saber?</h3>
              <p class="chat-hero-sub">Pergunte qualquer coisa sobre o manual "{{ chatManualName }}" — o EstagIArio Joe caça a resposta e cita a página.</p>
            </div>
            <div v-for="(msg, i) in chatMessages" :key="i" class="chat-msg" :class="msg.role === 'user' ? 'chat-msg--user' : 'chat-msg--ia'">
              <div class="chat-bubble">{{ msg.text }}</div>
            </div>
            <div v-if="chatLoading" class="chat-msg chat-msg--ia">
              <div class="chat-bubble chat-bubble--typing"><span/><span/><span/></div>
            </div>
          </div>

          <div class="chat-input-bar">
            <textarea
              v-model="chatInput"
              class="chat-textarea"
              rows="1"
              placeholder="Ex: qual o torque dos parafusos do braço principal?"
              :disabled="chatLoading"
              @keydown.enter.exact.prevent="sendQuestion"
            />
            <button class="chat-send" type="button" :disabled="chatLoading || !chatInput.trim()" @click="sendQuestion">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
          <p v-if="chatError" class="chat-error">{{ chatError }}</p>
        </template>
      </div>
    </template>

    <!-- ══ MODAL: Upload (fecha só pelo botão Cancelar — clique fora não fecha) ══ -->
    <div v-if="showUpload" class="modal-backdrop">
      <div class="modal-card">
        <h3 class="modal-title">Enviar manual (PDF)</h3>
        <div class="mf-field">
          <label class="mf-label">Nome do manual</label>
          <input v-model="uploadName" class="mf-input" type="text" placeholder="Ex: Manual O&M — Roda Gigante XYZ" />
        </div>
        <div class="mf-field">
          <label class="mf-label">Arquivo PDF (máx. 100MB)</label>
          <input ref="fileInput" class="mf-file" type="file" accept="application/pdf,.pdf" @change="onFilePick" />
          <p v-if="uploadFile" class="mf-hint">
            {{ uploadFile.name }} — {{ formatSize(uploadFile.size) }}
            <span v-if="uploadFile.size > COMPRESS_TARGET_BYTES" class="mf-hint-compress">· será comprimido para até 40MB antes do envio</span>
          </p>
        </div>
        <div class="mf-field">
          <label class="mf-label">Obras vinculadas</label>
          <div class="project-checks">
            <label v-for="proj in projects" :key="proj.id" class="project-check">
              <input type="checkbox" :value="proj.id" v-model="uploadProjectIds" />
              {{ proj.name }}
            </label>
          </div>
        </div>
        <p v-if="uploadError" class="modal-error">{{ uploadError }}</p>
        <p v-if="uploading && compressStatus" class="mf-compress-status">
          <span class="mf-compress-spinner" />
          {{ compressStatus }}
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="cancelOrClose">Cancelar</button>
          <button class="btn-primary" type="button" :disabled="uploading || !uploadFile || uploadProjectIds.length === 0" @click="doUpload">
            {{ uploading ? 'Processando...' : 'Enviar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Editar manual ══ -->
    <div v-if="editingManual" class="modal-backdrop" @click.self="editingManual = null">
      <div class="modal-card">
        <h3 class="modal-title">Editar manual</h3>
        <div class="mf-field">
          <label class="mf-label">Nome</label>
          <input v-model="editName" class="mf-input" type="text" />
        </div>
        <div class="mf-field">
          <label class="mf-label">Obras vinculadas</label>
          <div class="project-checks">
            <label v-for="proj in projects" :key="proj.id" class="project-check">
              <input type="checkbox" :value="proj.id" v-model="editProjectIds" />
              {{ proj.name }}
            </label>
          </div>
        </div>
        <p v-if="editError" class="modal-error">{{ editError }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="editingManual = null">Cancelar</button>
          <button class="btn-primary" type="button" :disabled="editProjectIds.length === 0" @click="saveEdit">Salvar</button>
        </div>
      </div>
    </div>

    <!-- ══ OVERLAY: Estagiário lendo ══ -->
    <div v-if="extracting" class="modal-backdrop modal-backdrop--ia">
      <div class="extract-loading">
        <div class="extract-spinner"/>
        <h3 class="extract-loading-title">O EstagIArio Joe está lendo o manual...</h3>
        <p class="extract-loading-sub">Interpretando rotinas de manutenção, periodicidades e checklists. Em manuais grandes isso pode levar alguns minutos.</p>
      </div>
    </div>

    <!-- ══ MODAL: Preview das rotinas extraídas ══ -->
    <div v-if="extractPreview" class="modal-backdrop" @click.self="askClosePreview">
      <div class="modal-card modal-card--wide">
        <h3 class="modal-title modal-title--ia">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          Rotinas encontradas no manual
        </h3>
        <p class="preview-sub">Revise, ajuste os responsáveis de cada grupo e salve. As rotinas serão criadas em <strong>{{ selectedProject?.name }}</strong>.</p>
        <p v-if="extractPreview.truncated" class="preview-truncated">
          Este manual é muito extenso — o estagiário pode não ter conseguido ler até o final. As rotinas abaixo são válidas, mas confira se falta algo das últimas seções do manual.
        </p>

        <div class="preview-groups">
          <div v-for="(grupo, gi) in extractPreview.grupos" :key="gi" class="preview-group" :class="{ 'preview-group--open': groupExpanded[gi] }">
            <div class="pg-header pg-header--clickable" @click="toggleGroup(gi)">
              <button class="pg-chevron" type="button" :title="groupExpanded[gi] ? 'Minimizar' : 'Expandir'" @click.stop="toggleGroup(gi)">
                <svg :class="{ open: groupExpanded[gi] }" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <input v-model="grupo.nome" class="pg-name-input" type="text" @click.stop />
              <span class="pg-count">{{ grupo.rotinas.length }} rotina{{ grupo.rotinas.length !== 1 ? 's' : '' }}</span>
              <span class="pg-peri">{{ periLabel(grupo.periodicidade) }}</span>
              <span v-if="!(groupResponsaveis[gi] && groupResponsaveis[gi].length)" class="pg-need-resp" title="Expanda para escolher os responsáveis">sem responsável</span>
              <button class="pg-remove" type="button" title="Remover grupo" @click.stop="removeGroup(gi)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <template v-if="groupExpanded[gi]">
            <div class="pg-resp">
              <span class="pg-resp-label">Responsáveis:</span>
              <div class="pg-resp-chips">
                <label v-for="acc in accounts" :key="acc.username" class="resp-chip" :class="{ active: groupResponsaveis[gi]?.includes(acc.username) }">
                  <input type="checkbox" :value="acc.username" v-model="groupResponsaveis[gi]" style="display:none" />
                  {{ acc.name || acc.username }}
                </label>
              </div>
              <p v-if="!groupResponsaveis[gi] || groupResponsaveis[gi].length === 0" class="pg-resp-warn">Selecione ao menos um responsável.</p>
            </div>

            <div v-for="(rotina, ri) in grupo.rotinas" :key="ri" class="preview-rotina">
              <div class="pr-head">
                <input v-model="rotina.nome" class="pr-name-input" type="text" />
                <span v-if="rotina.paginaReferencia" class="pr-page">pág. {{ rotina.paginaReferencia }}</span>
                <button class="pg-remove" type="button" title="Remover rotina" @click="grupo.rotinas.splice(ri, 1)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <textarea v-model="rotina.descricao" class="pr-desc" rows="2" placeholder="Descrição do procedimento" />
              <div v-if="rotina.checklist.length" class="pr-checklist">
                <div class="pr-check-title">Checklist:</div>
                <ul class="pr-check-list">
                  <li v-for="(item, ci) in rotina.checklist" :key="ci">
                    <input v-model="rotina.checklist[ci]" class="pr-check-input" type="text" />
                    <button class="pr-check-x" type="button" @click="rotina.checklist.splice(ci, 1)">×</button>
                  </li>
                </ul>
              </div>
              <div class="pr-types">
                <span class="pr-types-label">Resposta:</span>
                <button v-for="t in (['check', 'texto', 'foto'] as const)" :key="t" class="type-chip" :class="{ active: rotina.responseTypes.includes(t) }" type="button" @click="toggleType(rotina, t)">
                  {{ t === 'check' ? 'Check' : t === 'texto' ? 'Texto' : 'Foto' }}
                </button>
                <span v-if="rotina.responsavelSugerido" class="pr-sugg">Sugestão do manual: {{ rotina.responsavelSugerido }}</span>
              </div>
            </div>
            </template>
          </div>
        </div>

        <p v-if="saveError" class="modal-error">{{ saveError }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" :disabled="savingRoutines" @click="askClosePreview">Descartar</button>
          <button class="btn-primary btn-primary--ia" type="button" :disabled="savingRoutines || !canSavePreview" @click="savePreview">
            {{ savingRoutines ? 'Criando rotinas...' : `Salvar ${totalPreviewRoutines} rotina${totalPreviewRoutines !== 1 ? 's' : ''}` }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Toast de sucesso ══ -->
    <transition name="toast-fade">
      <div v-if="toast" class="manuals-toast">{{ toast }}</div>
    </transition>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { projectsStore, type Project } from '@/store/projects'
import { manualsStore, type MaintenanceManual, type ExtractResult, type ExtractedRoutine } from '@/store/manuals'
import { maintenanceStore } from '@/store/maintenance'
import { accountsStore } from '@/store/accounts'
import { compressPdfToTarget, PdfCompressCancelled, type PdfCancelToken, type PdfCompressProgress } from '@/utils/pdfCompress'

const MAX_PICK_BYTES = 100 * 1024 * 1024      // aceito no front
const COMPRESS_TARGET_BYTES = 40 * 1024 * 1024 // máximo que chega ao servidor

export default defineComponent({
  name: 'MaintenanceManualsPanel',
  props: {
    isAdmin: { type: Boolean, default: false }
  },
  setup() {
    const router = useRouter()
    const loadingProjects = ref(true)
    const selectedProject = ref<Project | null>(null)
    const activeTab = ref<'manuais' | 'duvidas'>('manuais')

    const projects = computed(() => projectsStore.all())
    const accounts = computed(() => accountsStore.all())
    const aiEnabled = computed(() => manualsStore.state.aiEnabled)

    const folderManuals = computed(() =>
      selectedProject.value
        ? manualsStore.state.items.filter(m => m.projectIds.includes(selectedProject.value!.id))
        : []
    )

    const manualCountFor = (projectId: string) =>
      manualsStore.state.items.filter(m => m.projectIds.includes(projectId)).length

    const formatSize = (bytes: number) => {
      if (!bytes) return '0 KB'
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    }
    const formatDate = (iso: string) => {
      if (!iso) return ''
      const d = new Date(iso)
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
    }

    const toast = ref('')
    let toastTimer: ReturnType<typeof setTimeout> | null = null
    const showToast = (msg: string) => {
      toast.value = msg
      if (toastTimer) clearTimeout(toastTimer)
      toastTimer = setTimeout(() => { toast.value = '' }, 4000)
    }

    // ── Navegação de pastas ──
    const openFolder = (proj: Project) => {
      selectedProject.value = proj
      activeTab.value = 'manuais'
    }
    const closeFolder = () => {
      selectedProject.value = null
      resetChat()
    }

    // ── Upload ──
    const showUpload = ref(false)
    const uploadName = ref('')
    const uploadFile = ref<File | null>(null)
    const uploadProjectIds = ref<string[]>([])
    const uploadError = ref('')
    const uploading = ref(false)
    const fileInput = ref<HTMLInputElement | null>(null)

    const openUploadModal = () => {
      uploadName.value = ''
      uploadFile.value = null
      uploadError.value = ''
      uploadProjectIds.value = selectedProject.value ? [selectedProject.value.id] : []
      showUpload.value = true
    }
    const compressStatus = ref('')
    let cancelToken: PdfCancelToken | null = null

    // Durante a compressão, "Cancelar" aborta o processamento; fora dela, fecha o modal
    const cancelOrClose = () => {
      if (uploading.value) {
        if (cancelToken) cancelToken.cancelled = true
        compressStatus.value = 'Cancelando...'
      } else {
        showUpload.value = false
      }
    }

    const onFilePick = (e: Event) => {
      uploadError.value = ''
      const files = (e.target as HTMLInputElement).files
      const file = files && files.length ? files[0] : null
      if (file && file.size > MAX_PICK_BYTES) {
        uploadError.value = 'O PDF excede 100MB. Divida o manual em partes menores.'
        uploadFile.value = null
        if (fileInput.value) fileInput.value.value = ''
        return
      }
      uploadFile.value = file
      if (uploadFile.value && !uploadName.value) {
        uploadName.value = uploadFile.value.name.replace(/\.pdf$/i, '')
      }
    }

    const onCompressProgress = (p: PdfCompressProgress) => {
      if (p.stage === 'analisando') compressStatus.value = 'Analisando o PDF...'
      else if (p.stage === 'otimizando') compressStatus.value = 'Otimizando o PDF (sem perda)...'
      else if (p.stage === 'imagens') compressStatus.value = `Comprimindo imagem ${p.page} de ${p.pages} (texto preservado)...`
      else if (p.stage === 'extraindo-texto') compressStatus.value = `Extraindo texto — página ${p.page} de ${p.pages}...`
      else if (p.stage === 'comprimindo') compressStatus.value = `Comprimindo — página ${p.page} de ${p.pages}${(p.pass || 1) > 1 ? ` (passe ${p.pass})` : ''}...`
      else if (p.stage === 'montando') compressStatus.value = 'Montando o PDF comprimido...'
    }

    const doUpload = async () => {
      if (!uploadFile.value || uploadProjectIds.value.length === 0) return
      uploading.value = true
      uploadError.value = ''
      compressStatus.value = ''
      cancelToken = { cancelled: false }
      try {
        // Comprime no navegador para chegar ao servidor com no máx. 40MB
        const result = await compressPdfToTarget(uploadFile.value, COMPRESS_TARGET_BYTES, onCompressProgress, cancelToken)
        compressStatus.value = 'Enviando ao servidor...'
        await manualsStore.upload(
          result.blob,
          uploadFile.value.name,
          uploadName.value.trim(),
          uploadProjectIds.value,
          result.rasterized ? { clientText: result.text, clientPageCount: result.pageCount } : undefined
        )
        showUpload.value = false
        showToast(
          result.wasCompressed
            ? `Manual enviado (comprimido de ${formatSize(result.originalBytes)} para ${formatSize(result.finalBytes)}).`
            : 'Manual enviado com sucesso.'
        )
      } catch (e) {
        if (e instanceof PdfCompressCancelled) {
          uploadError.value = '' // cancelamento silencioso — volta ao formulário
        } else {
          uploadError.value = e instanceof Error ? e.message : 'Erro ao enviar o manual.'
        }
      } finally {
        uploading.value = false
        compressStatus.value = ''
        cancelToken = null
      }
    }

    // ── Editar / excluir ──
    const editingManual = ref<MaintenanceManual | null>(null)
    const editName = ref('')
    const editProjectIds = ref<string[]>([])
    const editError = ref('')

    const openEditModal = (manual: MaintenanceManual) => {
      editingManual.value = manual
      editName.value = manual.name
      editProjectIds.value = [...manual.projectIds]
      editError.value = ''
    }
    const saveEdit = async () => {
      if (!editingManual.value) return
      try {
        await manualsStore.update(editingManual.value.id, { name: editName.value.trim(), projectIds: editProjectIds.value })
        editingManual.value = null
        showToast('Manual atualizado.')
      } catch (e) {
        editError.value = e instanceof Error ? e.message : 'Erro ao salvar.'
      }
    }
    const askRemove = async (manual: MaintenanceManual) => {
      if (!window.confirm(`Excluir o manual "${manual.name}"? O arquivo PDF será removido.`)) return
      try {
        await manualsStore.remove(manual.id)
        showToast('Manual excluído.')
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'Erro ao excluir.')
      }
    }
    const viewPdf = async (manual: MaintenanceManual) => {
      try { await manualsStore.openPdf(manual.id) } catch (e) {
        showToast(e instanceof Error ? e.message : 'Erro ao abrir o PDF.')
      }
    }

    // ── "Enviar para o estagiário" (extração de rotinas) ──
    const extracting = ref(false)
    const extractPreview = ref<ExtractResult | null>(null)
    const groupResponsaveis = ref<string[][]>([])
    // Grupos do preview vêm TODOS minimizados; clique no cabeçalho expande
    const groupExpanded = ref<boolean[]>([])
    const toggleGroup = (gi: number) => {
      groupExpanded.value[gi] = !groupExpanded.value[gi]
    }
    const savingRoutines = ref(false)
    const saveError = ref('')

    // Resultado do "enviar para o estagiário" do manual NO projeto aberto
    const extractionFor = (manual: MaintenanceManual) =>
      selectedProject.value ? manual.extractions?.[selectedProject.value.id] || null : null

    const goToRoutines = () => {
      if (selectedProject.value) router.push('/manutencao/' + encodeURIComponent(selectedProject.value.id))
    }

    const sendToEstagiario = async (manual: MaintenanceManual) => {
      extracting.value = true
      saveError.value = ''
      try {
        const result = await manualsStore.extract(manual.id)
        extractPreview.value = result
        groupResponsaveis.value = result.grupos.map(() => [])
        groupExpanded.value = result.grupos.map(() => false) // todos minimizados
        if (accountsStore.all().length === 0) await accountsStore.refresh()
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'Erro ao interpretar o manual.')
      } finally {
        extracting.value = false
      }
    }

    const periLabel = (p: string) => {
      const map: Record<string, string> = {
        diario: 'Diária', semanal: 'Semanal', quinzenal: 'Quinzenal', mensal: 'Mensal',
        trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual'
      }
      return map[p] || p
    }
    const toggleType = (rotina: ExtractedRoutine, t: 'check' | 'texto' | 'foto') => {
      const idx = rotina.responseTypes.indexOf(t)
      if (idx >= 0) {
        if (rotina.responseTypes.length > 1) rotina.responseTypes.splice(idx, 1)
      } else {
        rotina.responseTypes.push(t)
      }
    }
    const removeGroup = (gi: number) => {
      extractPreview.value?.grupos.splice(gi, 1)
      groupResponsaveis.value.splice(gi, 1)
      groupExpanded.value.splice(gi, 1)
    }
    const askClosePreview = () => {
      if (window.confirm('Descartar as rotinas encontradas? Você precisará enviar o manual para o estagiário novamente.')) {
        extractPreview.value = null
      }
    }

    const totalPreviewRoutines = computed(() =>
      (extractPreview.value?.grupos || []).reduce((sum, g) => sum + g.rotinas.length, 0)
    )
    const canSavePreview = computed(() => {
      const p = extractPreview.value
      if (!p || !p.grupos.length || totalPreviewRoutines.value === 0) return false
      return p.grupos.every((_, gi) => (groupResponsaveis.value[gi] || []).length > 0)
    })

    const savePreview = async () => {
      const preview = extractPreview.value
      const proj = selectedProject.value
      if (!preview || !proj) return
      savingRoutines.value = true
      saveError.value = ''
      try {
        for (let gi = 0; gi < preview.grupos.length; gi++) {
          const grupo = preview.grupos[gi]
          if (!grupo.rotinas.length) continue
          const items = grupo.rotinas.map(rotina => {
            let description = rotina.descricao || ''
            if (rotina.checklist.length) {
              description += `${description ? '\n\n' : ''}Checklist:\n` + rotina.checklist.map(c => `- ${c}`).join('\n')
            }
            if (rotina.paginaReferencia) {
              description += `\n\nReferência: manual "${preview.manualName}", pág. ${rotina.paginaReferencia}.`
            }
            return { title: rotina.nome, description }
          })
          await maintenanceStore.createServiceOrder({
            projectId: proj.id,
            projectName: proj.name,
            title: grupo.nome,
            description: `Criado pelo EstagIArio Joe a partir do manual "${preview.manualName}".`,
            priority: 'normal',
            responsavelUserIds: groupResponsaveis.value[gi] || [],
            items
          })
        }
        const total = totalPreviewRoutines.value
        // Registra o resultado — o botão "Enviar para o estagiário" some
        // neste projeto e dá lugar ao resultado no card do manual
        await manualsStore.markExtracted(preview.manualId, proj.id, total).catch(() => { /* não bloqueia o fluxo */ })
        extractPreview.value = null
        showToast(`${total} rotina${total !== 1 ? 's' : ''} criada${total !== 1 ? 's' : ''} em ${proj.name}. Veja em Manutenção > Visão Geral.`)
      } catch (e) {
        saveError.value = e instanceof Error ? e.message : 'Erro ao criar as rotinas.'
      } finally {
        savingRoutines.value = false
      }
    }

    // ── Chat "Alguma dúvida?" ──
    const chatManualId = ref('')
    const chatMessages = ref<Array<{ role: 'user' | 'model'; text: string }>>([])
    const chatInput = ref('')
    const chatLoading = ref(false)
    const chatError = ref('')
    const chatScroll = ref<HTMLElement | null>(null)

    const chatManualName = computed(() =>
      folderManuals.value.find(m => m.id === chatManualId.value)?.name || ''
    )

    const openDuvidas = () => {
      activeTab.value = 'duvidas'
      if (!chatManualId.value && folderManuals.value.length) {
        chatManualId.value = folderManuals.value[0].id
      }
    }
    const switchChatManual = (id: string) => {
      if (chatManualId.value !== id) {
        chatManualId.value = id
        chatMessages.value = []
        chatError.value = ''
      }
    }
    const resetChat = () => {
      chatManualId.value = ''
      chatMessages.value = []
      chatInput.value = ''
      chatError.value = ''
    }
    const scrollChatDown = () => {
      nextTick(() => {
        if (chatScroll.value) chatScroll.value.scrollTop = chatScroll.value.scrollHeight
      })
    }
    const sendQuestion = async () => {
      const q = chatInput.value.trim()
      if (!q || chatLoading.value || !chatManualId.value) return
      chatError.value = ''
      chatMessages.value.push({ role: 'user', text: q })
      chatInput.value = ''
      chatLoading.value = true
      scrollChatDown()
      try {
        const history = chatMessages.value.slice(0, -1).slice(-8)
        const answer = await manualsStore.ask(chatManualId.value, q, history)
        chatMessages.value.push({ role: 'model', text: answer })
      } catch (e) {
        chatError.value = e instanceof Error ? e.message : 'Erro ao consultar a IA.'
        chatMessages.value.pop()
        chatInput.value = q
      } finally {
        chatLoading.value = false
        scrollChatDown()
      }
    }

    onMounted(async () => {
      try {
        if (projectsStore.all().length === 0) await projectsStore.refresh()
        await Promise.all([
          manualsStore.load(),
          manualsStore.refreshAiStatus()
        ])
        if (accountsStore.all().length === 0) accountsStore.refresh().catch(() => { /* ignore */ })
      } finally {
        loadingProjects.value = false
      }
    })

    return {
      loadingProjects, selectedProject, activeTab, projects, accounts, aiEnabled,
      folderManuals, manualCountFor, formatSize, formatDate, toast,
      openFolder, closeFolder, manualsStore,
      showUpload, uploadName, uploadFile, uploadProjectIds, uploadError, uploading, fileInput,
      compressStatus, COMPRESS_TARGET_BYTES, cancelOrClose,
      openUploadModal, onFilePick, doUpload,
      editingManual, editName, editProjectIds, editError, openEditModal, saveEdit, askRemove, viewPdf,
      extracting, extractPreview, groupResponsaveis, groupExpanded, toggleGroup, savingRoutines, saveError,
      sendToEstagiario, extractionFor, goToRoutines, periLabel, toggleType, removeGroup, askClosePreview,
      totalPreviewRoutines, canSavePreview, savePreview,
      chatManualId, chatMessages, chatInput, chatLoading, chatError, chatScroll, chatManualName,
      openDuvidas, switchChatManual, sendQuestion
    }
  }
})
</script>

<style scoped lang="scss">
$ia-primary: #7c3aed;
$ia-light: #a78bfa;

.manuals-panel {
  width: 100%; height: 100%;
  background: var(--bg, #f7f4ee);
  display: flex; flex-direction: column;
  overflow-y: auto;
  padding: 24px 28px;
  box-sizing: border-box;
  position: relative;
}

.manuals-header { display: flex; flex-direction: column; gap: 4px; margin-bottom: 20px; }
.manuals-title { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; display: flex; align-items: center; gap: 8px; }
.beta-tag {
  font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  background: rgba(124,58,237,.12); color: $ia-primary;
  border: 1px solid rgba(124,58,237,.35); border-radius: 999px; padding: 2px 8px;
}
.manuals-subtitle { font-size: 13px; color: var(--ink-muted); margin: 0; }

.manuals-loading, .manuals-empty { font-size: 13px; color: var(--ink-muted); padding: 32px 0; text-align: center; }

/* ── Pastas ── */
.folder-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; align-items: start;
}
.folder-card {
  display: flex; align-items: center; gap: 14px;
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 14px;
  padding: 16px 18px; cursor: pointer; text-align: left;
  transition: box-shadow .15s, border-color .15s, transform .12s;
  &:hover { border-color: $ia-primary; box-shadow: 0 4px 16px rgba(0,0,0,.08); transform: translateY(-2px); }
}
.folder-icon { color: #d4a017; flex-shrink: 0; }
.folder-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.folder-name { font-size: 14px; font-weight: 700; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.folder-count { font-size: 12px; color: var(--ink-muted); }

/* ── Pasta aberta ── */
.folder-header { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.folder-back {
  display: flex; align-items: center; gap: 4px;
  background: none; border: none; color: var(--ink-muted); font-size: 13px; cursor: pointer;
  padding: 4px 6px; border-radius: 4px;
  &:hover { background: rgba(0,0,0,.05); }
}
.folder-title { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0; }

.folder-tabs { display: flex; gap: 4px; border-bottom: 1px solid rgba(31,30,26,.1); margin-bottom: 16px; }
.folder-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; border: none; background: none;
  font-size: 13px; font-weight: 600; color: var(--ink-muted); cursor: pointer;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  &.active { color: var(--accent); border-bottom-color: var(--accent); }
  &--ia.active { color: $ia-primary; border-bottom-color: $ia-primary; }
}

.folder-body-area { display: flex; flex-direction: column; gap: 14px; flex: 1; }
.manuals-actions { display: flex; }
.btn-upload {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 18px; border-radius: 10px; border: none; cursor: pointer;
  background: var(--accent, #0f4c81); color: #fff; font-size: 13px; font-weight: 700;
  &:hover { opacity: .9; }
}

/* ── Cards de manual ── */
.manual-list { display: flex; flex-direction: column; gap: 10px; }
.manual-card {
  display: flex; align-items: center; gap: 14px;
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 12px;
  padding: 14px 16px; flex-wrap: wrap;
}
.manual-icon { color: #dc2626; flex-shrink: 0; }
.manual-info { flex: 1; min-width: 180px; display: flex; flex-direction: column; gap: 3px; }
.manual-name { font-size: 14px; font-weight: 700; color: var(--ink); }
.manual-meta { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.manual-meta--sub { font-size: 11px; opacity: .8; }
.meta-sep { opacity: .5; }
.compressed-tag, .scan-tag {
  font-size: 10px; font-weight: 700; border-radius: 999px; padding: 1px 7px;
}
.compressed-tag { background: rgba(22,163,74,.12); color: #16a34a; }
.scan-tag { background: rgba(217,119,6,.12); color: #b45309; }

.manual-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.m-act {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(31,30,26,.15); border-radius: 8px; background: none; cursor: pointer;
  color: var(--ink-muted);
  &:hover { border-color: var(--accent); color: var(--accent); }
  &--danger:hover { border-color: #dc2626; color: #dc2626; }
}
.btn-estagiario {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: 9px; cursor: pointer;
  border: 1px solid rgba(124,58,237,.4);
  background: rgba(124,58,237,.08); color: $ia-primary;
  font-size: 12.5px; font-weight: 700;
  transition: background .15s;
  &:hover:not(:disabled) { background: rgba(124,58,237,.16); }
  &:disabled { opacity: .55; cursor: default; }
}

/* Resultado do envio ao estagiário (substitui o botão) */
.estagiario-result {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 12px; border-radius: 9px;
  border: 1px solid rgba(22,163,74,.35);
  background: rgba(22,163,74,.08); color: #15803d;
  font-size: 12px; font-weight: 600;
}
.btn-ver-rotinas {
  padding: 7px 13px; border-radius: 9px; cursor: pointer;
  border: 1px solid rgba(124,58,237,.4);
  background: none; color: $ia-primary;
  font-size: 12px; font-weight: 700;
  &:hover { background: rgba(124,58,237,.08); }
}
.btn-reenviar {
  padding: 6px 8px; border: none; background: none; cursor: pointer;
  color: var(--ink-muted); font-size: 11.5px; text-decoration: underline;
  &:hover:not(:disabled) { color: $ia-primary; }
  &:disabled { opacity: .5; cursor: default; }
}

/* ── Chat "Alguma dúvida?" ── */
.duvidas-area { display: flex; flex-direction: column; flex: 1; min-height: 380px; }
.duvidas-manual-picker { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.picker-label { font-size: 12px; color: var(--ink-muted); font-weight: 600; }
.picker-chip {
  padding: 5px 13px; border-radius: 999px; cursor: pointer; font-size: 12px; font-weight: 600;
  border: 1px solid rgba(124,58,237,.3); background: none; color: var(--ink-muted);
  &.active { background: rgba(124,58,237,.1); color: $ia-primary; border-color: $ia-primary; }
}
.chat-messages {
  flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 10px;
  padding: 6px 2px 14px; min-height: 240px;
}
.chat-hero { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 44px 20px; text-align: center; }
.chat-hero-icon {
  width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: rgba(124,58,237,.1); color: $ia-primary;
}
.chat-hero-title { font-size: 17px; font-weight: 700; color: var(--ink); margin: 0; }
.chat-hero-sub { font-size: 13px; color: var(--ink-muted); margin: 0; max-width: 420px; }

.chat-msg { display: flex; }
.chat-msg--user { justify-content: flex-end; }
.chat-msg--ia { justify-content: flex-start; }
.chat-bubble {
  max-width: 76%; padding: 10px 14px; border-radius: 14px; font-size: 13.5px; line-height: 1.5;
  white-space: pre-wrap; word-break: break-word;
  .chat-msg--user & { background: $ia-primary; color: #fff; border-bottom-right-radius: 4px; }
  .chat-msg--ia & { background: var(--card, #fff); color: var(--ink); border: 1px solid rgba(31,30,26,.1); border-bottom-left-radius: 4px; }
}
.chat-bubble--typing {
  display: flex; gap: 4px; align-items: center; padding: 14px 16px;
  span { width: 7px; height: 7px; border-radius: 50%; background: $ia-light; animation: typing-blink 1.2s infinite; }
  span:nth-child(2) { animation-delay: .2s; }
  span:nth-child(3) { animation-delay: .4s; }
}
@keyframes typing-blink { 0%, 60%, 100% { opacity: .3; } 30% { opacity: 1; } }

.chat-input-bar {
  display: flex; align-items: flex-end; gap: 8px;
  background: var(--card, #fff); border: 1px solid rgba(124,58,237,.3); border-radius: 14px;
  padding: 10px 12px;
}
.chat-textarea {
  flex: 1; border: none; outline: none; resize: none; background: none;
  font-size: 13.5px; color: var(--ink); font-family: inherit; line-height: 1.4; max-height: 120px;
}
.chat-send {
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  border: none; border-radius: 10px; cursor: pointer;
  background: $ia-primary; color: #fff;
  &:disabled { opacity: .4; cursor: default; }
  &:hover:not(:disabled) { opacity: .88; }
}
.chat-error { font-size: 12px; color: #dc2626; margin: 6px 2px 0; }

/* ── Modais ── */
.modal-backdrop {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(20,18,14,.45); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; padding: 20px;
  &--ia { background: rgba(46,16,101,.5); }
}
.modal-card {
  background: var(--card, #fff); border-radius: 16px; padding: 22px 24px;
  width: 100%; max-width: 460px; max-height: 88vh; overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
  box-shadow: 0 18px 50px rgba(0,0,0,.25);
  &--wide { max-width: 760px; }
}
.modal-title { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0; display: flex; align-items: center; gap: 8px; }
.modal-title--ia { color: $ia-primary; }
.modal-error { font-size: 12.5px; color: #dc2626; margin: 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; }
.btn-secondary {
  padding: 9px 18px; border-radius: 9px; border: 1px solid rgba(31,30,26,.2);
  background: none; color: var(--ink-muted); font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { background: rgba(0,0,0,.04); }
}
.btn-primary {
  padding: 9px 20px; border-radius: 9px; border: none;
  background: var(--accent, #0f4c81); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: .5; cursor: default; }
  &:hover:not(:disabled) { opacity: .9; }
  &--ia { background: $ia-primary; }
}

.mf-field { display: flex; flex-direction: column; gap: 5px; }
.mf-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }
.mf-input {
  border: 1px solid rgba(31,30,26,.15); border-radius: 9px; padding: 9px 12px;
  font-size: 13px; color: var(--ink); background: var(--bg, #fff); outline: none;
  &:focus { border-color: $ia-primary; }
}
.mf-file { font-size: 13px; color: var(--ink); }
.mf-hint { font-size: 12px; color: var(--ink-muted); margin: 0; }
.mf-hint-compress { color: #7c3aed; font-weight: 600; }
.mf-compress-status {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; color: #7c3aed; font-weight: 600; margin: 0;
}
.mf-compress-spinner {
  width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid rgba(124,58,237,.25); border-top-color: #7c3aed;
  animation: spin 0.9s linear infinite;
}
.project-checks { display: flex; flex-direction: column; gap: 6px; max-height: 160px; overflow-y: auto; }
.project-check {
  display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink); cursor: pointer;
  input { accent-color: #7c3aed; }
}

/* ── Overlay de extração ── */
.extract-loading {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  background: var(--card, #fff); border-radius: 18px; padding: 36px 40px; max-width: 420px; text-align: center;
}
.extract-spinner {
  width: 42px; height: 42px; border-radius: 50%;
  border: 4px solid rgba(124,58,237,.2); border-top-color: #7c3aed;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.extract-loading-title { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0; }
.extract-loading-sub { font-size: 13px; color: var(--ink-muted); margin: 0; }

/* ── Preview de rotinas ── */
.preview-sub { font-size: 13px; color: var(--ink-muted); margin: 0; }
.preview-truncated {
  font-size: 12.5px; color: #b45309; margin: 0;
  background: rgba(217,119,6,.08); border: 1px solid rgba(217,119,6,.25);
  border-radius: 8px; padding: 8px 12px;
}
.preview-groups { display: flex; flex-direction: column; gap: 16px; }
.preview-group {
  border: 1px solid rgba(124,58,237,.25); border-radius: 12px; padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px; background: rgba(124,58,237,.03);
}
.pg-header { display: flex; align-items: center; gap: 10px; }
.pg-header--clickable { cursor: pointer; user-select: none; }
.pg-chevron {
  width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  border: none; background: none; color: #7c3aed; cursor: pointer; border-radius: 6px;
  svg { transition: transform .15s; }
  svg.open { transform: rotate(90deg); }
  &:hover { background: rgba(124,58,237,.1); }
}
.pg-count { font-size: 11.5px; color: var(--ink-muted); font-weight: 600; white-space: nowrap; }
.pg-need-resp {
  font-size: 10.5px; font-weight: 700; white-space: nowrap;
  background: rgba(217,119,6,.12); color: #b45309;
  border: 1px solid rgba(217,119,6,.3); border-radius: 999px; padding: 2px 8px;
}
.pg-name-input {
  flex: 1; border: 1px solid transparent; border-radius: 8px; padding: 6px 10px;
  font-size: 14px; font-weight: 700; color: var(--ink); background: none; outline: none;
  &:hover, &:focus { border-color: rgba(124,58,237,.35); background: var(--card, #fff); }
}
.pg-peri {
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  background: rgba(124,58,237,.12); color: #7c3aed; border-radius: 999px; padding: 3px 10px; flex-shrink: 0;
}
.pg-remove {
  width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  border: none; background: none; color: var(--ink-muted); cursor: pointer; border-radius: 6px;
  &:hover { color: #dc2626; background: rgba(220,38,38,.08); }
}
.pg-resp { display: flex; flex-direction: column; gap: 6px; }
.pg-resp-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }
.pg-resp-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.resp-chip {
  padding: 4px 12px; border-radius: 999px; cursor: pointer; font-size: 12px; font-weight: 600;
  border: 1px solid rgba(31,30,26,.18); color: var(--ink-muted); user-select: none;
  &.active { background: #7c3aed; color: #fff; border-color: #7c3aed; }
}
.pg-resp-warn { font-size: 11.5px; color: #b45309; margin: 0; }

.preview-rotina {
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 10px;
  padding: 10px 12px; display: flex; flex-direction: column; gap: 8px;
}
.pr-head { display: flex; align-items: center; gap: 8px; }
.pr-name-input {
  flex: 1; border: 1px solid transparent; border-radius: 7px; padding: 5px 8px;
  font-size: 13px; font-weight: 700; color: var(--ink); background: none; outline: none;
  &:hover, &:focus { border-color: rgba(124,58,237,.3); }
}
.pr-page { font-size: 11px; color: #7c3aed; font-weight: 600; flex-shrink: 0; }
.pr-desc {
  border: 1px solid rgba(31,30,26,.12); border-radius: 8px; padding: 7px 10px;
  font-size: 12.5px; color: var(--ink); background: var(--bg, #fdfcfa); outline: none; resize: vertical;
  font-family: inherit; line-height: 1.45;
  &:focus { border-color: rgba(124,58,237,.4); }
}
.pr-checklist { display: flex; flex-direction: column; gap: 4px; }
.pr-check-title { font-size: 11.5px; font-weight: 700; color: var(--ink-muted); }
.pr-check-list {
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 3px;
  li { display: flex; align-items: center; gap: 6px; }
}
.pr-check-input {
  flex: 1; border: 1px solid transparent; border-radius: 6px; padding: 3px 8px;
  font-size: 12px; color: var(--ink); background: none; outline: none;
  &:hover, &:focus { border-color: rgba(31,30,26,.18); background: var(--bg, #fdfcfa); }
}
.pr-check-x {
  border: none; background: none; color: var(--ink-muted); cursor: pointer; font-size: 14px;
  width: 20px; height: 20px; border-radius: 5px; line-height: 1;
  &:hover { color: #dc2626; background: rgba(220,38,38,.08); }
}
.pr-types { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.pr-types-label { font-size: 11.5px; font-weight: 600; color: var(--ink-muted); }
.type-chip {
  padding: 3px 11px; border-radius: 999px; cursor: pointer; font-size: 11.5px; font-weight: 600;
  border: 1px solid rgba(31,30,26,.18); background: none; color: var(--ink-muted);
  &.active { background: rgba(124,58,237,.1); color: #7c3aed; border-color: #7c3aed; }
}
.pr-sugg { font-size: 11px; color: var(--ink-muted); font-style: italic; margin-left: auto; }

/* ── Toast ── */
.manuals-toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 80;
  background: #1f1e1a; color: #fff; font-size: 13px; font-weight: 600;
  border-radius: 10px; padding: 11px 20px; box-shadow: 0 8px 24px rgba(0,0,0,.3);
  max-width: 90vw; text-align: center;
}
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity .25s, transform .25s; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(8px); }

@media (max-width: 640px) {
  .manuals-panel { padding: 16px 14px; }
  .manual-card { flex-direction: column; align-items: stretch; }
  .manual-actions { justify-content: flex-end; }
}
</style>
